import React, { useState } from "react";
import { Send, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { FileUpload } from "./FileUpload";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { toast } from "sonner";

export const ApplyTaskDialog = ({ open, onOpenChange, task, profile, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    proposal: "",
    relevant_skills: (profile?.skills || []).slice(0, 4),
    experience: profile?.graduation_year ? `Graduating ${profile.graduation_year}` : "",
    availability: "Immediately",
    portfolio_url: "",
    portfolio_files: [],
  });

  const submit = async () => {
    if (!f.proposal.trim()) { toast.error("Proposal is required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tasks/apply", {
        task_id: task.task_id || task.id,
        proposal: f.proposal,
        relevant_skills: f.relevant_skills,
        experience: f.experience,
        availability: f.availability,
        portfolio_url: f.portfolio_url || null,
        portfolio_file_ids: f.portfolio_files.map(x => x.file_id),
      });
      toast.success("Application submitted", { description: `Match score: ${data.match_score ?? "—"}%` });
      onDone?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply");
    } finally { setBusy(false); }
  };

  const toggleSkill = (s) => setF(x => ({ ...x, relevant_skills: x.relevant_skills.includes(s) ? x.relevant_skills.filter(y => y !== s) : [...x.relevant_skills, s] }));

  const skillPool = Array.from(new Set([...(task?.skills || []), ...(profile?.skills || [])]));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" style={{ background: "var(--surface)", borderColor: "var(--border-strong)", color: "#F8F9FA" }}>
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Apply for task</DialogTitle>
          <DialogDescription className="text-zinc-500">
            {task?.title} · {task?.company || task?.company_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Proposal">
            <textarea data-testid="apply-proposal" rows={4} value={f.proposal} onChange={e => setF(x => ({ ...x, proposal: e.target.value }))}
                      placeholder="Why are you the right fit? Mention specific experience and how you'll approach this task."
                      className={ic + " resize-none"}/>
          </Field>

          <Field label="Relevant skills">
            <div className="flex flex-wrap gap-1.5">
              {skillPool.length === 0 && <span className="text-xs text-zinc-500">No skills on profile — add some in onboarding.</span>}
              {skillPool.map(s => {
                const on = f.relevant_skills.includes(s);
                return <button key={s} data-testid={`apply-skill-${s}`} type="button" onClick={() => toggleSkill(s)}
                               className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors duration-200 ${on ? "border-blue-500/40 bg-blue-500/10 text-blue-300" : "border-[#27272A] text-zinc-400 hover:border-[#3A3A42]"}`}>
                  {s}
                </button>;
              })}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Experience">
              <input data-testid="apply-experience" value={f.experience} onChange={e => setF(x => ({ ...x, experience: e.target.value }))} className={ic}/>
            </Field>
            <Field label="Availability">
              <select data-testid="apply-availability" value={f.availability} onChange={e => setF(x => ({ ...x, availability: e.target.value }))} className={ic + " appearance-none"}>
                <option>Immediately</option>
                <option>Within a week</option>
                <option>Within 2 weeks</option>
                <option>Later</option>
              </select>
            </Field>
          </div>

          <Field label="Portfolio link (optional)">
            <input data-testid="apply-portfolio-url" value={f.portfolio_url} onChange={e => setF(x => ({ ...x, portfolio_url: e.target.value }))} placeholder="https://" className={ic}/>
          </Field>

          <FileUpload
            value={null}
            onChange={v => v && setF(x => ({ ...x, portfolio_files: [...x.portfolio_files, v] }))}
            purpose="portfolio"
            testId="apply-portfolio-file"
            label="Attach a work sample (optional)"
            hint="Choose file to attach"
          />
          {f.portfolio_files.length > 0 && (
            <div className="text-[11px] text-zinc-500">{f.portfolio_files.length} file{f.portfolio_files.length !== 1 && "s"} attached</div>
          )}
        </div>

        <DialogFooter>
          <button onClick={() => onOpenChange(false)} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
          <button data-testid="apply-confirm-btn" onClick={submit} disabled={busy} className="btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={13} className="animate-spin"/> : <Send size={13}/>}
            Submit application
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ic = "w-full px-3 py-2.5 rounded-md border bg-[#111114] text-sm text-white placeholder:text-zinc-600 outline-none transition-colors duration-200 focus:border-blue-500/50 border-[#27272A]";
const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

export default ApplyTaskDialog;
